import { Mail, MapPin, Phone, RotateCcw, CreditCard } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useState } from "react";


import logo from "../assets/images/logo.png";
import logoMD from "../assets/images/Logo_MD.webp";
import logoRound from "../assets/icons/top_logo.webp";
import facebookIcon from "../assets/icons/facebookicon.webp";
import instagramIcon from "../assets/icons/igicon.webp";
import linkedinIcon from "../assets/icons/linkedinicon.webp";
import tiktokIcon from "../assets/icons/tiktokicon.webp";
import threadIcon from "../assets/icons/threadicon.webp";
import topLogo from "../assets/icons/top_logo.webp";

const contactItems = [
  {
    icon: MapPin,
    text: "Level 1, Menara Amplewest, 6, Jalan P. Ramlee, Kuala Lumpur, 50250 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur.",
    href: "https://www.google.com/maps/search/?api=1&query=Level+1,+Menara+Amplewest,+6,+Jalan+P.+Ramlee,+Kuala+Lumpur,+50250",
  },
  {
    icon: Phone,
    text: "+60 11 6509 5910",
    href: "https://wa.me/601165095910?text=Hi%20Wesitex%2C%20I'm%20interested%20to%20know%20more%20about%20your%20services.",
  },
  {
    icon: Mail,
    text: "info@wesitex.com",
    href: "mailto:info@wesitex.com",
  },
];

const socials = [
  { name: "Threads", icon: threadIcon, href: "https://www.threads.net/@wesitex", imgScale: 0.9, rotation: -12 },
  { name: "Facebook", icon: facebookIcon, href: "https://www.facebook.com/wesitexsoft", imgScale: 0.9, rotation: 8 },
  { name: "TikTok", icon: tiktokIcon, href: "https://www.tiktok.com/@wesitex", imgScale: 0.9, rotation: -8 },
  { name: "Instagram", icon: instagramIcon, href: "https://www.instagram.com/wesitex/", imgScale: 0.9, rotation: 12 },
  { name: "LinkedIn", icon: linkedinIcon, href: "https://www.linkedin.com/company/wesitex", imgScale: 0.9, rotation: -4 },
];

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const riseVariants: Variants = {
  hidden: { opacity: 0, y: 26, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.75, ease: "easeOut" },
  },
};

export default function Footer({
  onNavigate
}: {
  onNavigate?: (page: "home" | "contact" | "education" | "property" | "technology" | "tourism" | "sports" | "engineering" | "about" | "socials", targetId?: string) => void;
}) {
  const [flipped, setFlipped] = useState(false);

  return (
    <footer className="relative pb-0 pt-0 w-full overflow-hidden" style={{ perspective: "1200px" }}>

      {/* Flip container */}
      <div
        className="relative w-full transition-transform duration-700 ease-in-out"
        style={{
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateX(180deg)" : "rotateX(0deg)",
          minHeight: "420px",
        }}
      >

        {/* ─── FRONT FACE ─── */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", pointerEvents: flipped ? "none" : "auto" }}
        >
          {/* Geometric Background */}
          <div className="absolute inset-0 w-full h-full z-0 bg-[#05030a] overflow-hidden pointer-events-none">
            <svg className="absolute right-0 bottom-0 w-full h-full" viewBox="0 0 1000 400" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="550,400 800,220 1050,400" fill="#b49bfa" />
              <polygon points="750,400 1000,150 1000,400" fill="#ffffff" />
            </svg>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="relative z-10 w-full h-full px-6 sm:px-12 md:px-20 lg:px-28 xl:px-36 py-8"
          >
            <div className="relative z-10 h-full flex flex-col justify-between">
              <div className="mt-4 flex flex-col gap-8">
                {/* Top row */}
                <div className="flex flex-col md:flex-row gap-8 sm:gap-12 justify-between items-start md:items-center w-full">

                  {/* Left: Logo + tagline + socials */}
                  <motion.div variants={riseVariants} className="flex-1 max-w-xl flex flex-col items-start">
                    {/* Logo */}
                    <div className="relative group/logo flex flex-col items-start cursor-pointer w-full max-w-152">
                      <span className="absolute bottom-2 left-6 w-[55%] h-2.5 rounded-[100%] bg-black/60 blur-xs sm:blur-[6px] pointer-events-none transition-all duration-500 ease-out group-hover/logo:[animation-play-state:paused] group-hover/logo:scale-x-[0.7] group-hover/logo:scale-y-[0.5] group-hover/logo:opacity-20 group-hover/logo:blur-sm animate-shadow-0" />
                      <motion.a
                        href="#"
                        onClick={(e) => { e.preventDefault(); onNavigate?.("home"); }}
                        aria-label="Back to top"
                        whileTap={{ scale: 0.96 }}
                        className="relative z-10 flex h-20 xs:h-24 sm:h-28 w-full items-center transition-transform duration-500 ease-out group-hover/logo:-translate-y-4 group-hover/logo:scale-[1.05]"
                      >
                        <div className="animate-float-0 group-hover/logo:[animation-play-state:paused] w-full h-full flex items-center relative">
                          <div className="absolute left-10 sm:left-14 top-1/2 -translate-y-1/2 w-48 xs:w-64 sm:w-80 h-24 xs:h-32 sm:h-40 bg-violet-600/25 blur-3xl rounded-full pointer-events-none z-0" />
                          <img src={logo} alt="Wesitex" className="relative z-10 h-full w-full object-contain object-left animate-[pulse_3s_ease-in-out_infinite] mix-blend-screen" style={{ imageRendering: '-webkit-optimize-contrast' }} />
                          <div className="absolute inset-0 z-20 pointer-events-none mix-blend-screen" style={{ WebkitMaskImage: `url(${logo})`, maskImage: `url(${logo})`, WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskPosition: 'left center', maskPosition: 'left center', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
                            <div className="absolute inset-0 w-[150%] h-full sync-shine-streak bg-linear-to-r from-transparent via-white/80 to-transparent mix-blend-overlay" />
                          </div>
                        </div>
                      </motion.a>
                    </div>

                    {/* Tagline */}
                    <p className="font-orbitron text-sm xs:text-base sm:text-xl md:text-2xl font-bold uppercase tracking-[0.2em] text-white/80 -mt-2 mb-4 sm:mb-6">
                      Leading in Ed-Tech
                    </p>

                    {/* Social icons under logo */}
                    <div className="flex items-center gap-6 xs:gap-8 sm:gap-10">
                      {socials.map((social, index) => (
                        <motion.a
                          key={social.name}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.name}
                          initial={{ opacity: 0, y: 18 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.45, delay: index * 0.05, ease: "easeOut" }}
                          className="group flex flex-col items-center gap-1.5 relative transform-[translateZ(0)]"
                        >
                          <div className="relative flex h-12 w-12 xs:h-14 xs:w-14 sm:h-16 sm:w-16 items-center justify-center">
                            <div className="absolute -bottom-1 left-0 right-0 flex justify-center">
                              <span className={`h-1 w-7 xs:w-9 sm:w-11 rounded-[100%] bg-black/60 blur-[2px] xs:blur-[4px] pointer-events-none transition-all duration-500 ease-out group-hover:[animation-play-state:paused] group-hover:scale-[0.6] group-hover:opacity-30 group-hover:blur-[6px] animate-shadow-${index}`} />
                            </div>
                            <div className="relative z-10 w-full h-full flex items-center justify-center transition-transform duration-500 ease-out group-hover:-translate-y-3 group-hover:scale-[1.12]">
                              <div className={`animate-float-${index} group-hover:[animation-play-state:paused] w-full h-full flex items-center justify-center`}>
                                <img
                                  src={social.icon}
                                  alt={social.name}
                                  className="h-full w-full object-contain transition-transform duration-500 ease-out [transform:scale(var(--scale))_rotate(var(--rotate))] group-hover:[--rotate:0deg] group-hover:[--scale:1.1]"
                                  style={{ 
                                    imageRendering: "crisp-edges",
                                    "--scale": social.imgScale,
                                    "--rotate": `${social.rotation}deg`
                                  } as React.CSSProperties}
                                />
                              </div>
                            </div>
                          </div>
                          <span className="text-[9px] xs:text-[10px] sm:text-xs font-medium text-white/40 text-center">{social.name}</span>
                        </motion.a>
                      ))}
                    </div>
                  </motion.div>

                  {/* Center: Get In Touch + Business Card Button */}
                  <motion.div variants={riseVariants} className="shrink-0 flex flex-col items-center justify-center gap-5 md:px-8">
                    <div className="text-center flex flex-col items-center gap-1">
                      <h3 className="font-orbitron font-black text-2xl sm:text-3xl md:text-4xl text-white tracking-widest uppercase drop-shadow-lg">
                        Get In Touch
                      </h3>
                      <p className="font-mono text-[10px] sm:text-xs text-brand-purple tracking-widest uppercase mt-1">
                        Let's Work Together
                      </p>
                    </div>

                    {/* View Business Card Button */}
                    <button
                      onClick={() => setFlipped(true)}
                      className="group flex items-center gap-2 font-mono text-xs sm:text-sm tracking-widest uppercase bg-white/10 border border-white/50 text-white px-6 py-3 hover:bg-white/20 hover:border-white transition-all duration-300 mt-2 whitespace-nowrap"
                    >
                      <CreditCard className="w-4 h-4 group-hover:text-brand-purple transition-colors" />
                      View Business Card
                    </button>
                  </motion.div>

                  {/* Right: Malaysia Digital */}
                  <motion.div variants={riseVariants} className="flex-1 flex flex-col items-center md:items-end justify-center">
                    <div className="text-center mb-2">
                      <img src={logoMD} alt="Malaysia Digital" className="h-14 xs:h-20 sm:h-28 md:h-32 w-auto object-contain drop-shadow-[0_0_15px_rgba(139,92,246,0.35)] invert brightness-0 opacity-80" />
                    </div>
                  </motion.div>
                </div>

               
              </div>

              <div className="mt-4 border-t border-white/10 pt-3 text-center text-[10px] sm:text-xs font-light tracking-wide text-white/50">
                Copyright © {new Date().getFullYear()} Wesitex. All rights reserved.
              </div>
            </div>
          </motion.div>
        </div>

        {/* ─── BACK FACE (Business Card) ─── */}
        <div
          className="absolute inset-0 w-full"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateX(180deg)",
          }}
        >
          {/* Same background pattern as front */}
          <div className="absolute inset-0 w-full h-full z-0 bg-[#05030a] overflow-hidden pointer-events-none">
            <svg className="absolute right-0 bottom-0 w-full h-full" viewBox="0 0 1000 400" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polygon points="550,400 800,220 1050,400" fill="#b49bfa" />
              <polygon points="750,400 1000,150 1000,400" fill="#ffffff" />
            </svg>
          </div>

          <div className="relative z-10 w-full min-h-105 flex flex-col md:flex-row justify-between items-center md:items-stretch px-6 sm:px-12 md:px-20 lg:px-28 xl:px-36 py-8">

            {/* Left: company name + contact list */}
            <div className="flex flex-col justify-between flex-1">
              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-orbitron font-black text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight tracking-tight">
                      Wesitex Sdn Bhd
                    </h3>
                    <p className="font-mono text-xs sm:text-sm md:text-base tracking-[0.25em] uppercase text-brand-purple mt-2">
                      Leading in Ed-Tech
                    </p>
                  </div>
                </div>
                <div className="h-px w-16 bg-brand-purple mb-5 sm:mb-7" />
              </div>

              <div className="flex flex-col gap-4 sm:gap-5">
                {[
                  { icon: MapPin, label: "Address", text: "1.1 Menara Amplewest, KL 50250", href: "https://www.google.com/maps/search/?api=1&query=Level+1,+Menara+Amplewest,+6,+Jalan+P.+Ramlee,+Kuala+Lumpur,+50250" },
                  { icon: Phone, label: "Phone", text: "+60 11 6509 5910", href: "https://wa.me/601165095910" },
                  { icon: Mail, label: "Email", text: "info@wesitex.com", href: "mailto:info@wesitex.com" },
                  { icon: null, label: "Website", text: "https://www.wesitex.com", href: "https://www.wesitex.com" },
                ].map((item, i) => (
                  <a key={i} href={item.href} target="_blank" rel="noopener noreferrer"
                    className="group flex items-start gap-4 hover:opacity-80 transition-opacity"
                  >
                    <div className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-brand-purple/30 mt-0.5">
                      {item.icon ? <item.icon className="h-4 w-4 text-brand-purple" /> : <span className="text-brand-purple text-xs font-bold">W</span>}
                    </div>
                    <div>
                      <p className="font-mono text-[10px] sm:text-xs text-brand-purple uppercase tracking-widest mb-0.5">{item.label}</p>
                      <p className="font-mono text-xs sm:text-sm md:text-base text-white/80 leading-snug">{item.text}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Center: Get In Touch + Flip Back */}
            <div className="shrink-0 flex flex-col items-center justify-center gap-5 md:px-8 mt-8 md:mt-0">
              <div className="text-center flex flex-col items-center gap-1">
                <h3 className="font-orbitron font-black text-2xl sm:text-3xl md:text-4xl text-white tracking-widest uppercase drop-shadow-lg">
                  Get In Touch
                </h3>
                <p className="font-mono text-[10px] sm:text-xs text-brand-purple tracking-widest uppercase mt-1">
                  Let's Work Together
                </p>
              </div>

              {/* Flip Back Button */}
              <button
                onClick={() => setFlipped(false)}
                className="group flex items-center gap-2 font-mono text-xs sm:text-sm tracking-widest uppercase bg-white/10 border border-white/50 text-white px-6 py-3 hover:bg-white/20 hover:border-white transition-all duration-300 mt-2 whitespace-nowrap"
              >
                <RotateCcw className="w-4 h-4 group-hover:-rotate-180 transition-transform duration-500" />
                Flip Back
              </button>
            </div>

            {/* Right: round logo fills the white triangle */}
            <div className="flex-1 flex items-center justify-center md:justify-end mt-8 md:mt-0">
              <img src={logoRound} alt="Wesitex" className="h-36 w-36 sm:h-48 sm:w-48 md:h-64 md:w-64 lg:h-72 lg:w-72 object-contain drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
